import { tradingApi } from "../api/trading";
import { TradingAccount, TradingAccountInfo, TradingAccountType, TradingOrderType, TradingPositionSide, TradingTickData, TradingTickerInfo } from "../config/trading";
import { EditingPosition } from "./editingPosition";
import { hub } from "../hub";
import { TraderConfig } from "./tradingHub";


class AccountPositions {
    long?: EditingPosition;
    short?: EditingPosition;
    constructor(public tid: string) {
    }
    get(side: TradingPositionSide) {
        return side == TradingPositionSide.Long ? this.long : this.short;
    }
    set(pos: EditingPosition) {
        if (pos.long)
            this.long = pos;
        else
            this.short = pos;
    }
    delete(pos: EditingPosition) {
        if (this.long == pos)
            this.long = null;
        else if (this.short == pos)
            this.short = null;
        return this.empty;
    }
    get empty() { return !this.long && !this.short; }
}
export class EditingAccount {

    private _accountInfo: TradingAccountInfo;
    private _account: TradingAccount;
    private _positions = new Map<string, AccountPositions>();
    private _config: TraderConfig;
    private _current: EditingPosition;

    get isPaper() { return this.account.id == 0; }
    get id() { return this.account.id; }
    get name() { return this.account.name; }
    get positions() { return this._positions; }
    get account() { return this._account; }
    get config() { return this._config; }
    get accountInfo() { return this._accountInfo; }
    get current() { return this._current; }

    constructor(account: TradingAccount, config: TraderConfig) {
        this._account = account;
        this._config = config;
    }
    updateAccount(account: TradingAccount) {
        this._account = account;
    }
    async refreshAccountInfo() {
        if (this.isPaper)
            this._accountInfo = {
                accountId: this._account.id,
                balance: 0,
                canTrade: false,
                positions: [],
                comission: {
                    maker: 0.02 / 100,
                    taker: 0.05 / 100
                }
            };
        else {
            this._accountInfo = await tradingApi.getAccountInfo(this._account.id);
            this._accountInfo.positions ??= [];
        }
        const longp = new Set<string>();
        const shortp = new Set<string>();
        for (const pos of this.accountInfo.positions) {
            if (pos.side == TradingPositionSide.Short)
                shortp.add(pos.ticker.id);
            else
                longp.add(pos.ticker.id);
            this.positions.delete(pos.ticker.id);
            const epos = new EditingPosition(this, this.config, pos);
            pos.orders ??= [];
            for (const order of pos.orders) {
                switch (order.type) {
                    case TradingOrderType.Entry:
                    case TradingOrderType.Limit:
                        epos.addEntry(order);
                        break;
                    case TradingOrderType.Stop:
                        epos.addStop(order);
                        break;
                    case TradingOrderType.TakeProfit:
                        epos.addTarget(order);
                        break;
                }
            }
            var apos = this.positions.get(pos.ticker.id);
            if (!apos)
                this.positions.set(pos.ticker.id, apos = new AccountPositions(pos.ticker.id));
            apos.set(epos);
            epos.sortTargets();
            let leftSize = epos.entry.size;
            for (let i = 0; i < epos.targets.length; i++) {
                if (epos.targets[i].trd.closePosition) {
                    epos.targets[i].fix = 100;
                    epos.targets[i].size = leftSize;
                    leftSize = 0;
                } else if (leftSize != 0) {
                    epos.targets[i].fix = 100 * epos.targets[i].size / leftSize;
                    leftSize -= epos.targets[i].size;
                } else {
                    epos.targets[i].fix = 0;
                }
            }
            epos.recalInfo();
        }
        for (const pos of Array.from(this.positions.values())) {
            if (pos.long && !pos.long.new)
                if (!longp.has(pos.long.tid))
                    pos.delete(pos.long);
            if (pos.short && !pos.short.new)
                if (!shortp.has(pos.short.tid))
                    pos.delete(pos.short);
            if (pos.empty)
                this.positions.delete(pos.tid);
        }
    }
    updateMarketPrice(tick: TradingTickData) {
        var pos = this._positions.get(tick.tid);
        if (pos) {
            pos.long?.updateMarketPrice(tick);
            pos.short?.updateMarketPrice(tick);
        }
    }

    public cancel(pos: EditingPosition) {
        if (pos) {
            const apos = this.positions.get(pos.tid);
            if (apos.delete(pos))
                this.positions.delete(apos.tid);
        } else
            for (let tid of Array.from(this.positions.keys()))
                this.positions.delete(tid);

        if (pos == this.current)
            this._current = null;
    }
    public select(pos: EditingPosition) {
        this._current = pos;
    }

    private getTickerInfo = async (tid: string) => {
        if (this.account.type == TradingAccountType.Virtual)
            return {
                accountId: this.account.id,
                balance: this.accountInfo.balance,
                canTrade: this.accountInfo.canTrade,
                comission: this.accountInfo.comission,
                lvg: 25,
                brakets: []
            } as TradingTickerInfo;
        return await tradingApi.getTickerInfo(this.account.id, tid);
    };
    public updatePos = async (pos: EditingPosition) => {
        pos.recalInfo();
        return tradingApi.updatePosition(pos.loadState()).then(x => {
            pos.restoreState(x);
        });
    }
    public addNew = async (tid: string, long: boolean) => {
        let apos = this.positions.get(tid);
        if (!apos)
            this.positions.set(tid, apos = new AccountPositions(tid));
        const side = long ? TradingPositionSide.Long : TradingPositionSide.Short;
        let pos = apos.get(side);

        const ticker = hub.tickers.get(tid);
        const tvd = hub.TVs.find(x => x.tid == tid);

        const tickerInfo = await this.getTickerInfo(tid);

        const priceRange = hub.first.tv.activeChart().getPanes()[0].getMainSourcePriceScale().getVisiblePriceRange();
        var priceDev = priceRange.to - priceRange.from;
        const entry: number = tvd.getBar(0).close;

        pos = new EditingPosition(this, this.config, {
            accountId: this.account.id,
            comission: this.accountInfo.comission,
            lvg: tickerInfo.lvg,
            markPrice: entry,
            side: side,
            ticker: ticker,
            new: true
        });
        apos.set(pos);
        pos.tf = tvd.getTimeFrameInfo();
        pos.brakets = tickerInfo.brakets;

        pos.addEntry({
            id: 0,
            market: true,
            price: entry,
            closePosition: false,
            type: TradingOrderType.Limit
        });

        const stop: number = entry + 10 * priceDev / 100 * (long ? -1 : 1);
        pos.addStop({
            id: 0,
            market: true,
            price: stop,
            type: TradingOrderType.Stop,
            closePosition: true
        });

        const target = entry + (entry - stop) * this.config.rr;
        pos.addTarget({
            id: 0,
            market: true,
            price: target,
            type: TradingOrderType.TakeProfit,
            closePosition: false
        });

        pos.recalInfo();
        this._current = pos;
        return pos;
    };
}
